import { createContext, ReactNode, useContext } from 'react';
import { useTenantFeaturesQuery } from '../hooks';
import type { TenantFeatureKey } from '../api/tenantFeatures';

type FeatureFlagContextProps = {
  features: TenantFeatureKey[];
  hasFeature: (feature: TenantFeatureKey) => boolean;
  isLoading: boolean;
};

type FeatureFlagProviderProps = {
  children?: ReactNode;
};

const FeatureFlagContext = createContext<FeatureFlagContextProps | undefined>(
  undefined,
);

/**
 * Provider for feature flags
 * Fetches enabled features for the tenant and provides a hook to check if a feature is enabled
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const { data: features = [], isLoading } = useTenantFeaturesQuery();

  const hasFeature = (feature: TenantFeatureKey): boolean => {
    return features.includes(feature);
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        features,
        hasFeature,
        isLoading,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags
 * Returns the list of enabled features and a helper function to check if a feature is enabled
 *
 * @example
 * const { hasFeature, isLoading } = useFeatureFlag();
 * if (hasFeature('CUSTOM_SURVEYS')) {
 *   // Show custom surveys UI
 * }
 */
export const useFeatureFlag = (): FeatureFlagContextProps => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureFlag must be used within a FeatureFlagProvider.',
    );
  }
  return context;
};
